<template>
  <div class="fsc" data-testid="font-scale-control">
    <div class="fsc__head">
      <p class="fsc__title">Text size</p>
      <button
        v-if="!isDefault"
        type="button"
        class="fsc__reset"
        data-testid="font-scale-reset"
        @click="commit(DEFAULT_SCALE)"
      >
        Reset
      </button>
    </div>

    <div class="fsc__row">
      <!-- End caps, sized off the ends of the range so the control shows what it
           does. aria-hidden: the slider itself carries the accessible name. -->
      <span class="fsc__cap fsc__cap--sm" aria-hidden="true">A</span>
      <input
        v-model.number="value"
        type="range"
        class="fsc__range"
        :min="MIN_SCALE"
        :max="MAX_SCALE"
        :step="STEP"
        data-testid="font-scale-slider"
        aria-label="Text size"
        :aria-valuetext="percent"
        @input="preview"
        @change="commit(value)"
      />
      <span class="fsc__cap fsc__cap--lg" aria-hidden="true">A</span>
    </div>
  </div>
</template>

<script>
import {
  DEFAULT_SCALE,
  MAX_SCALE,
  MIN_SCALE,
  STEP,
  fontScaleState,
  previewScale,
  setPreference,
} from '@/composables/useFontScale';

export default {
  name: 'FontScaleControl',
  data() {
    // Local, because dragging previews without committing: binding the input
    // straight to `preference` would snap the thumb back on every frame.
    return { value: 1, MIN_SCALE, MAX_SCALE, STEP, DEFAULT_SCALE };
  },
  computed: {
    // Read straight off the module-level ref — no setup(), per AGENTS.md.
    preference() {
      return fontScaleState.preference.value;
    },
    // Screen readers only, via aria-valuetext. There is deliberately no visible
    // readout: a percentage is not what a member is choosing — the lesson text
    // behind the panel is — and repainting a number on every step of the drag
    // was one more thing changing in a control that already felt unsteady.
    percent() {
      return `${Math.round(this.value * 100)}%`;
    },
    isDefault() {
      return Math.abs(this.value - this.DEFAULT_SCALE) < 0.001;
    },
  },
  watch: {
    // Keep the thumb honest if the size is changed elsewhere (the dev harness).
    preference: {
      immediate: true,
      handler(v) {
        this.value = Number(v) || 1;
      },
    },
  },
  methods: {
    // While dragging: show it, do not store it.
    preview() {
      previewScale(this.value);
    },
    // On release: store it. `change` is the release event for a range input, so
    // localStorage is written once per drag rather than once per frame.
    commit(v) {
      this.value = v;
      setPreference(v);
    },
  },
};
</script>

<style scoped>
/* px, not rem, throughout: this panel is chrome rendered inside a route that is
   itself scaled, so rem here would grow the control with the very setting it
   configures — at the top of the range it outgrew the viewport.

   Every var() fallback below is theme()-derived rather than a literal. The
   fallbacks are load-bearing, not decoration: the /font-scale harness mounts this
   control on a bare <ion-page> with no .courses-theme ancestor, so they ARE the
   colours it renders. Written as literals they drift silently, and three of them
   already had — ink #2b2926 against a palette that says #3D3A35, ink-muted
   #6b6b6b against #938272, and the border fallback #e0d9d1 against #E2DDD4.
   theme() is resolved at build time, so the output is still a plain hex. */
.fsc {
  /* 48px, not 3rem — see the note above. In rem this term grew with the scale
     being dragged, so on a 375pt phone the panel (and with it the slider track)
     shrank by ~17px mid-drag and the thumb slid out from under the finger. */
  width: min(320px, calc(100vw - 48px));
}
.fsc__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.fsc__title {
  font-size: 15px;
  font-weight: 700;
  color: var(--courses-ink, theme('colors.ink'));
}
.fsc__reset {
  font-size: 12px;
  font-weight: 600;
  color: var(--courses-ink-muted, theme('colors.brown.400'));
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}
.fsc__row {
  /* Was the hint paragraph's margin-bottom (ONE-8285 removed the hint). Kept on
     the row so the slider still clears the title rather than crowding it. */
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.fsc__cap {
  flex: none;
  font-weight: 700;
  line-height: 1;
  color: var(--courses-ink, theme('colors.ink'));
}
.fsc__cap--sm {
  font-size: 13px;
}
.fsc__cap--lg {
  font-size: 24px;
}
.fsc__range {
  flex: 1;
  min-width: 0;
  /* 44px of vertical hit area around a 4px track — the thumb is the whole
     control on touch, and a hairline track is not grabbable. */
  height: 44px;
  margin: 0;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
}
/* `track`, not `border`: in dark mode --courses-border is #37332F, the very
   colour of the panel this sits on, so the track vanished. --courses-track is the
   token for exactly this — the unfilled part of a progress/slider rail — and it
   flips to a legible #646160. In light it moves the rail from #E2DDD4 to #D8D1CB,
   matching the lesson progress bar on the landing page. */
.fsc__range::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 999px;
  background: var(--courses-track, theme('colors.courses.track'));
}
/* Same story for the knob: fill and border were surface-raised and border, both
   #37332F in dark, leaving only the box-shadow to hint at a thumb. */
.fsc__range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 26px;
  height: 26px;
  margin-top: -11px; /* centres the thumb on the 4px track */
  border: 1px solid var(--courses-track, theme('colors.courses.track'));
  border-radius: 999px;
  background: var(--courses-handle, theme('colors.courses.surface-raised'));
  box-shadow: 0 1px 4px rgb(0 0 0 / 25%);
}
</style>
